/**
 * Число слушателей программы или пакета в заявке (training-listeners-count; ТЗ 26.10.2026, п.1.3):
 * клиент может просто указать количество и при желании выбрать конкретных сотрудников. «−» и «+»
 * сохраняют сразу; набранное число — по уходу с поля или Enter. Меньше выбранных сотрудников
 * указать нельзя — их сначала убирают из выбора.
 *
 * Ответ заказчицы 04.11 к п.1.4: когда все места названы, «−» не гаснет, а открывает выбор, кого из
 * сотрудников убрать (`onRemoveListeners`); число мест после этого пересчитывается само.
 */
import { useEffect, useState } from 'react'
import { Minus, Plus } from 'lucide-react'
import { Button } from '@/design-system/components/primitives/button'
import { Input } from '@/design-system/components/primitives/input'

/** Верхняя граница числа слушателей (как на сервере). */
export const MAX_LISTENERS = 10000

export function ListenersCountInput({
  label,
  value,
  min,
  disabled,
  onChange,
  onRemoveListeners,
}: {
  /** Чьё число — для доступных имён поля и кнопок («программа «…»», «пакет «…»»). */
  label: string
  value: number
  /** Не меньше выбранных сотрудников и не меньше одного. */
  min: number
  disabled?: boolean
  onChange: (next: number) => void
  /** «−» при всех названных местах: выбрать, кого убрать. Нет — «−» гаснет на минимуме. */
  onRemoveListeners?: () => void
}) {
  // На минимуме «−» убирает сотрудника, если места заняты названными слушателями (min > 1 или
  // единственное место названо — тогда min = 1 и выбирающий решает, кого снять).
  const removeMode = value <= min && Boolean(onRemoveListeners)
  const [draft, setDraft] = useState(String(value))
  // Поле показывает сохранённое число: после ответа сервера (value) и после неудачного сохранения
  // (disabled снимается, value прежний) набранное, но не сохранённое число не остаётся в поле.
  useEffect(() => {
    if (!disabled) setDraft(String(value))
  }, [value, disabled])

  function commit(raw: string) {
    const parsed = Number(raw)
    if (raw.trim() === '' || !Number.isInteger(parsed)) {
      setDraft(String(value))
      return
    }
    const next = Math.min(MAX_LISTENERS, Math.max(min, parsed))
    setDraft(String(next))
    if (next !== value) onChange(next)
  }

  return (
    <div className="flex items-center gap-1">
      <Button
        type="button"
        size="sm"
        variant="secondary"
        aria-label={
          removeMode ? `Убрать сотрудников — ${label}` : `Уменьшить число слушателей — ${label}`
        }
        title={removeMode ? 'Выбрать, кого из сотрудников убрать' : undefined}
        disabled={disabled || (value <= min && !removeMode)}
        onClick={() => (removeMode ? onRemoveListeners?.() : onChange(value - 1))}
      >
        <Minus className="size-4 stroke-current" aria-hidden />
      </Button>
      <Input
        type="number"
        inputMode="numeric"
        min={min}
        max={MAX_LISTENERS}
        aria-label={`Число слушателей — ${label}`}
        className="ds-no-spin w-16 text-center tabular-nums"
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit(e.currentTarget.value)
          }
        }}
      />
      <Button
        type="button"
        size="sm"
        variant="secondary"
        aria-label={`Увеличить число слушателей — ${label}`}
        disabled={disabled || value >= MAX_LISTENERS}
        onClick={() => onChange(value + 1)}
      >
        <Plus className="size-4 stroke-current" aria-hidden />
      </Button>
    </div>
  )
}
